import { Pressable, View } from 'react-native'
import React, { useEffect, useState } from 'react'
import Typography from '@ui/typography'
import Animated, {
  interpolate,
  interpolateColor,
  useAnimatedStyle,
} from 'react-native-reanimated'
import { Collapsible } from '@ui/animated/Collapsible'
import useAnimatedToggle from '@ui/hooks/useAnimatedToggle'
import { ChecklistStep } from './Checklist'
import Button from '@ui/buttons/Button'
import cn from '@ui/utils/cn'
import PressableIcon from '@ui/buttons/PressableIcon'
import CheckListNumber from './CheckListNumber'
import { animationConfig, getColorsArray, lineColor, textColor } from './styles'
import useChecklistStateAnimation from './useChecklistItemStateAnimation'
import { getChecklistItemStateNumberArray } from './types'
import { useAnimatedOpacity } from '@ui/animated/useAnimatedOpacity'

interface Props extends ChecklistStep {
  index: number
  step?: number
  setStep?: (s: number) => void
}

export default function ChecklistStepComponent({
  node,
  nodeChecked,
  title,
  desc,
  button,
  index,
  last,
  state = 'upcoming',
  staticBody,
  hideLine,
  showLineOpen,
  hideNumber,
  defaultOpen,
  ...props
}: Props) {
  const animation = useChecklistStateAnimation(state, animationConfig)
  const animationSteps = getChecklistItemStateNumberArray
  const lineColors = getColorsArray(lineColor)
  const textColors = getColorsArray(textColor)

  const [mounted, setMounted] = useState(false)
  const [open, setOpen] = useState(defaultOpen ?? false)
  const [openDone, setOpenDone] = useState(false)

  const isExpandable =
    props.isExpandable !== undefined
      ? props.isExpandable
      : state === 'todo' || (state === 'done' && (!!nodeChecked || staticBody))

  const isOpenAnimation = useAnimatedToggle(open || openDone)
  const isExpandableAnimation = useAnimatedToggle(!!isExpandable)

  const toggle = () => setOpenDone((prev) => !prev)

  const isLineVisible =
    (showLineOpen && (open || openDone)) || (!last && !hideLine)
  const styleLineOpacity = useAnimatedOpacity(isLineVisible)
  const styleLine = useAnimatedStyle(() => {
    return {
      backgroundColor: interpolateColor(
        animation.value,
        animationSteps,
        lineColors
      ),
    }
  })

  const styleTitle = useAnimatedStyle(() => {
    return {
      color: interpolateColor(animation.value, animationSteps, textColors),
    }
  })

  const styleIcon = useAnimatedStyle(() => {
    return {
      opacity: interpolate(isExpandableAnimation.value, [0, 1], [0, 1]),
      transform: [
        {
          rotate: interpolate(isOpenAnimation.value, [0, 1], [0, 180]) + 'deg',
        },
      ],
    }
  })

  useEffect(() => {
    if (mounted) return
    setMounted(true)
  }, [])

  useEffect(() => {
    if (state === 'todo') {
      setOpen(true)
      setOpenDone(false)
    }
    if (state === 'done') {
      setOpen(false)
      setOpenDone(false)
    }
    if (state === 'upcoming' || state === 'locked') {
      setOpen(false)
      setOpenDone(false)
    }
  }, [state])

  const body = (
    <View
      className={cn('flex flex-col gap-8 pt-2', {
        'pb-12': !node && desc,
        'pb-8': node && desc,
      })}
    >
      {!!desc && (
        <Typography variant="MD" className="color-light">
          {desc}
        </Typography>
      )}
      {!!button && (
        <Button
          size="md"
          variant={'outlined'}
          className="w-max self-start"
          {...button}
        />
      )}
      {!!node && node}
    </View>
  )

  return (
    <Animated.View className="flex-0 ios:flex-1 flex w-full flex-row gap-8 pb-3 pr-0">
      <View className="flex flex-col items-center gap-3">
        <CheckListNumber
          state={state || 'upcoming'}
          index={index}
          onPress={toggle}
          hideNumber={hideNumber}
        />

        <Animated.View
          className="rounded-xs w-[3px] flex-1"
          style={[styleLine, styleLineOpacity]}
        />
      </View>
      <Animated.View className="flex-1 flex-col gap-2">
        <Pressable
          className="flex flex-row justify-between gap-0"
          onPress={toggle}
          disabled={!isExpandable}
        >
          <Typography variant="XLB" style={styleTitle}>
            {title}
          </Typography>
          <View className="h-8 w-8">
            {isExpandable && (
              <PressableIcon
                name="chevron-down"
                className="h-8 w-8"
                color={textColor[state]}
                onPress={toggle}
                style={[styleIcon]}
              />
            )}
          </View>
        </Pressable>
        <View className="min-h-8 pr-16">
          {!staticBody && (
            <Collapsible fullWidth open={mounted && open}>
              {body}
            </Collapsible>
          )}
          {staticBody && (
            <Collapsible fullWidth open={openDone || open}>
              {body}
            </Collapsible>
          )}
          {nodeChecked && (
            <Collapsible fullWidth open={openDone && state === 'done'}>
              <View className={cn('flex w-full flex-col gap-4 pb-12 pt-2')}>
                {nodeChecked}
              </View>
            </Collapsible>
          )}
        </View>
      </Animated.View>
    </Animated.View>
  )
}
